import "../styles/app.css";
import { api, ApiError } from "./api";
import { iniciarRouter, navegar, type Ruta } from "./router";
import type { Sesion } from "../lib/types";
import { renderLogin } from "./pages/login";
import { renderPedido } from "./pages/pedido";
import { renderPedidosWeb } from "./pages/pedidosweb";
import { renderPedidos } from "./pages/pedidos";
import { renderProduccion } from "./pages/produccion";
import { renderCuentas } from "./pages/cuentas";
import { renderClientes } from "./pages/clientes";
import { renderProductos } from "./pages/productos";
import { renderCategorias } from "./pages/categorias";
import { renderListaPrecios } from "./pages/listaprecios";
import { renderSubas } from "./pages/subas";
import { renderInformes } from "./pages/informes";
import { renderUsuarios } from "./pages/usuarios";
import { renderEmpresa } from "./pages/empresa";
import { renderAuditoria } from "./pages/auditoria";
import { renderAyuda } from "./pages/ayuda";
import { renderClientePedido } from "./pages/clientepedido";
import { renderClientePedidos } from "./pages/clientepedidos";
import { renderClienteCuenta } from "./pages/clientecuenta";
import { renderAyudaCliente } from "./pages/ayudacliente";
import { renderAvisos } from "./pages/avisos";
import {
  registrarServiceWorker,
  botonAvisosHtml,
  engancharBotonAvisos,
  refrescarContador,
  escucharPush,
} from "./notificaciones";
import { engancharBotonesTema } from "./tema";
import {
  logoApp,
  iconoMenu,
  iconoCerrar,
  iconoRuta,
  iconoFlechaDer,
  iconoAyuda,
  iconoCampana,
  iconoChevron,
  iconoUsuario,
} from "./iconos";

const nav = document.getElementById("nav") as HTMLElement;
const app = document.getElementById("app") as HTMLElement;

// Navegacion de administracion (personal de la confiteria).
const RUTAS: Ruta[] = [
  { hash: "#/pedido", titulo: "Pedido", render: renderPedido, permiso: "pedido" },
  { hash: "#/pedidos-web", titulo: "Pedidos web", render: renderPedidosWeb, permiso: "pedidosweb" },
  { hash: "#/pedidos", titulo: "Pedidos", render: renderPedidos, permiso: "pedidos" },
  { hash: "#/produccion", titulo: "Producción", render: renderProduccion, permiso: "produccion" },
  { hash: "#/cuentas", titulo: "Cuentas", render: renderCuentas, permiso: "cuentas" },
  { hash: "#/listas", titulo: "Lista de precios", render: renderListaPrecios, permiso: "listas" },
  { hash: "#/subas", titulo: "Subas", render: renderSubas, permiso: "subas" },
  { hash: "#/informes", titulo: "Informes", render: renderInformes, permiso: "informes" },
  { hash: "#/clientes", titulo: "Clientes", render: renderClientes, permiso: "clientes" },
  { hash: "#/productos", titulo: "Productos", render: renderProductos, permiso: "productos" },
  { hash: "#/categorias", titulo: "Categorías", render: renderCategorias, permiso: "categorias" },
  { hash: "#/usuarios", titulo: "Usuarios", render: renderUsuarios, permiso: "__admin" },
  { hash: "#/empresa", titulo: "Empresa", render: renderEmpresa, permiso: "__admin" },
  { hash: "#/auditoria", titulo: "Auditoría", render: renderAuditoria, permiso: "__admin" },
  { hash: "#/ayuda", titulo: "Centro de ayuda", render: renderAyuda, oculta: true },
  { hash: "#/avisos", titulo: "Avisos", render: renderAvisos, oculta: true },
];

// Navegacion del local (rol CLIENTE). Es OTRO juego de rutas, no un filtro del
// de arriba: el local no tiene por que conocer las pantallas de administracion.
const RUTAS_CLIENTE: Ruta[] = [
  { hash: "#/pedir", titulo: "Hacer pedido", render: renderClientePedido },
  { hash: "#/mispedidos", titulo: "Mis pedidos", render: renderClientePedidos },
  { hash: "#/micuenta", titulo: "Mi cuenta", render: renderClienteCuenta },
  { hash: "#/ayuda", titulo: "Cómo usar la app", render: renderAyudaCliente, oculta: true },
  { hash: "#/avisos", titulo: "Avisos", render: renderAvisos, oculta: true },
];

// Como se ordena la barra de ESCRITORIO. Con las 14 pantallas en fila, la
// barra se partia en tres renglones. Ahora queda en uno: a la vista lo de
// todos los dias, y el resto en dos desplegables.
//
// Es solo presentacion: que rutas ve cada usuario lo sigue decidiendo
// rutasPermitidas(). Un grupo muestra unicamente las suyas que esten
// permitidas; si le queda una sola se muestra suelta, y si no le queda
// ninguna, no aparece. Una ruta que no este en ningun grupo va suelta, asi una
// pantalla nueva nunca desaparece del menu por olvidarse de anotarla aca.
const GRUPOS_NAV: Array<{ titulo: string; hashes: string[] }> = [
  { titulo: "Catálogo", hashes: ["#/productos", "#/categorias", "#/listas", "#/subas", "#/clientes"] },
  { titulo: "Ajustes", hashes: ["#/usuarios", "#/empresa", "#/auditoria"] },
];

// Rutas que el usuario logueado puede usar (admin ve todo).
function rutasPermitidas(s: Sesion): Ruta[] {
  if (s.rol === "CLIENTE") return RUTAS_CLIENTE;
  if (s.es_admin) return RUTAS;
  return RUTAS.filter(
    (r) => !r.permiso || (r.permiso !== "__admin" && s.permisos.includes(r.permiso)),
  );
}

function marcarActivo(): void {
  const actual = location.hash || "#/pedido";
  document.querySelectorAll<HTMLAnchorElement>("a[data-hash]").forEach((a) => {
    a.classList.toggle("activo", a.dataset.hash === actual);
  });
  // Un desplegable queda subrayado si la pantalla actual esta adentro: si no,
  // estando en Usuarios la barra no diria en que parte del sistema estas.
  document.querySelectorAll<HTMLElement>("[data-grupo]").forEach((g) => {
    g.querySelector("[data-grupo-boton]")?.classList.toggle(
      "activo",
      g.querySelector(`a[data-hash="${actual}"]`) !== null,
    );
  });
}

/** Cierra todos los desplegables de la barra (menos `salvo`, si se pasa). */
function cerrarDesplegables(salvo?: Element | null): void {
  document.querySelectorAll<HTMLElement>("[data-grupo]").forEach((g) => {
    if (g === salvo) return;
    g.querySelector("[data-grupo-panel]")?.classList.add("hidden");
    g.querySelector("[data-grupo-boton]")?.setAttribute("aria-expanded", "false");
  });
}

function renderNav(s: Sesion, rutas: Ruta[]): void {
  const esCliente = s.rol === "CLIENTE";
  // La marca es la misma para todos: el logo de la confiteria y su nombre. El
  // local para el que se esta pidiendo se ve donde hace falta (el selector y el
  // subtitulo de la pantalla de pedido), no en la barra.
  const marca = `${logoApp()}<span>Piedra y Sol</span>`;
  const visibles = rutas.filter((r) => !r.oculta);
  const linkSuelto = (r: Ruta): string =>
    `<a href="${r.hash}" data-hash="${r.hash}" class="nav-desktop whitespace-nowrap border-b-2 border-transparent px-2 py-1 font-medium text-masa-600 transition hover:text-masa-900">${r.titulo}</a>`;

  const desplegable = (titulo: string, items: Ruta[]): string => `
    <div class="relative" data-grupo>
      <button type="button" data-grupo-boton aria-expanded="false" aria-haspopup="true"
        class="nav-desktop flex items-center gap-1 whitespace-nowrap border-b-2 border-transparent px-2 py-1 font-medium text-masa-600 transition hover:text-masa-900">
        ${titulo}<span class="text-masa-400 [&>svg]:h-3.5 [&>svg]:w-3.5">${iconoChevron}</span>
      </button>
      <div data-grupo-panel class="absolute left-0 top-full z-20 mt-2 hidden min-w-[13rem] rounded-xl border border-crema-200 bg-papel p-1 shadow-lg">
        ${items
          .map(
            (r) => `<a href="${r.hash}" data-hash="${r.hash}" class="nav-sub flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-masa-700 hover:bg-crema-100">
              <span class="text-masa-400">${iconoRuta(r.hash)}</span>${r.titulo}</a>`,
          )
          .join("")}
      </div>
    </div>`;

  // Cada grupo va en el lugar de su primera pantalla; las sueltas, donde estaban.
  const yaPuesto = new Set<string>();
  const linksDesktop = visibles
    .map((r) => {
      if (yaPuesto.has(r.hash)) return "";
      const grupo = GRUPOS_NAV.find((g) => g.hashes.includes(r.hash));
      if (!grupo) return linkSuelto(r);
      const items = grupo.hashes
        .map((h) => visibles.find((v) => v.hash === h))
        .filter((v): v is Ruta => v !== undefined);
      items.forEach((i) => yaPuesto.add(i.hash));
      return items.length === 1 ? linkSuelto(items[0]) : desplegable(grupo.titulo, items);
    })
    .join("");
  const linksMovil = visibles
    .map(
      (r) =>
        `<a href="${r.hash}" data-hash="${r.hash}" class="nav-movil flex items-center gap-3 rounded-xl px-4 py-4 text-lg font-medium text-masa-800 hover:bg-crema-100 active:bg-crema-200">
          <span class="text-masa-500">${iconoRuta(r.hash)}</span>
          <span class="flex-1">${r.titulo}</span>
          <span class="text-masa-300">${iconoFlechaDer}</span>
        </a>`,
    )
    .join("");

  // Cada rol tiene su propia ayuda: la de administracion explica los modulos
  // segun sus permisos; la del local explica como pedir e instalar la app.
  const tituloAyuda = esCliente ? "Cómo usar la app" : "Centro de ayuda";
  const botonAyuda = `<a href="#/ayuda" data-hash="#/ayuda" title="${tituloAyuda}" class="rounded-lg p-1.5 text-masa-500 hover:bg-crema-100 hover:text-masa-800">${iconoAyuda}</a>`;

  // La campana esta SIEMPRE, con avisos o sin ellos: si apareciera solo cuando
  // hay algo, no habria forma de volver a leer lo de ayer. El area de toque es
  // de 44x44 (h-11 w-11), que es lo minimo comodo para el pulgar; el numero va
  // encima, en la esquina, y solo cuando hay pendientes.
  const campana = `
    <a href="#/avisos" data-hash="#/avisos" title="Avisos"
       class="relative flex h-11 w-11 items-center justify-center rounded-lg text-masa-500 hover:bg-crema-100 hover:text-masa-800">
      ${iconoCampana}
      <span id="avisosNumero"
        class="absolute right-1 top-1 hidden min-w-[1.15rem] rounded-full bg-acento-600 px-1 text-center text-xs font-bold leading-[1.15rem] text-white"></span>
    </a>`;

  // Desde `lg` (1024px) va la barra de escritorio; mas angosto, el menu ☰. Entre
  // medio no hay una barra a medias: en una tablet parada las pantallas no
  // entran en un renglon, y partirla en dos es justo lo que se quiso sacar.
  const quien = s.email ?? s.usuario;
  nav.innerHTML = `
    <div class="mx-auto max-w-5xl px-4">
      <div class="flex items-center gap-2 py-2.5">
        <span class="flex flex-1 items-center gap-1.5 whitespace-nowrap text-lg font-bold text-masa-900 lg:mr-2 lg:flex-none">
          ${marca}
        </span>
        <nav class="hidden flex-1 items-center gap-1 text-sm lg:flex">${linksDesktop}</nav>
        ${campana}
        ${botonAyuda}
        <div class="relative hidden lg:block" data-grupo>
          <button type="button" data-grupo-boton aria-expanded="false" aria-haspopup="true" title="${quien}"
            class="flex h-9 w-9 items-center justify-center rounded-full bg-crema-100 text-masa-600 hover:bg-crema-200 hover:text-masa-900">${iconoUsuario}</button>
          <div data-grupo-panel class="absolute right-0 top-full z-20 mt-2 hidden min-w-[15rem] rounded-xl border border-crema-200 bg-papel p-1 shadow-lg">
            <div class="border-b border-crema-100 px-3 py-2">
              <span class="block text-[11px] font-semibold uppercase tracking-wider text-masa-400">Sesión</span>
              <span class="block truncate text-sm font-medium text-masa-800">${quien}</span>
            </div>
            <button type="button" data-tema-boton class="mt-1 flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-masa-700 hover:bg-crema-100"></button>
            <button id="salir" class="block w-full rounded-lg px-3 py-2 text-left text-sm text-masa-700 hover:bg-crema-100">Salir</button>
          </div>
        </div>
        <button id="hamb" class="rounded-lg p-2 text-masa-600 hover:bg-crema-100 lg:hidden" aria-label="Abrir menú">${iconoMenu}</button>
      </div>
    </div>`;

  // Desplegables: se abren con un clic y se cierran al elegir, al tocar afuera
  // o con Escape. De a uno: abrir uno cierra el otro.
  nav.querySelectorAll<HTMLElement>("[data-grupo]").forEach((g) => {
    const boton = g.querySelector("[data-grupo-boton]") as HTMLElement;
    const panel = g.querySelector("[data-grupo-panel]") as HTMLElement;
    boton.addEventListener("click", (ev) => {
      ev.stopPropagation();
      cerrarDesplegables(g);
      const abierto = !panel.classList.toggle("hidden");
      boton.setAttribute("aria-expanded", String(abierto));
    });
    panel.querySelectorAll("a").forEach((a) => a.addEventListener("click", () => cerrarDesplegables()));
  });

  // El overlay va en el body (no dentro del header, que tiene backdrop-blur y
  // crearia un bloque contenedor que rompe el position:fixed).
  document.getElementById("menuMovil")?.remove();
  const menuMovil = document.createElement("div");
  menuMovil.id = "menuMovil";
  menuMovil.className = "fixed inset-0 z-50 hidden lg:hidden";
  menuMovil.innerHTML = `
      <div id="backdrop" class="absolute inset-0 bg-black/40"></div>
      <div class="absolute inset-x-0 top-0 max-h-full overflow-y-auto rounded-b-3xl bg-crema-50 shadow-2xl">
        <div class="flex items-center justify-between border-b border-crema-200 px-4 py-3">
          <span class="flex items-center gap-1.5 text-lg font-bold text-masa-900">
            ${marca}
          </span>
          <button id="cerrarMenu" class="chip-accion" aria-label="Cerrar">${iconoCerrar}</button>
        </div>
        <nav class="flex flex-col gap-1 p-2">${linksMovil}
          <a href="#/ayuda" data-hash="#/ayuda" class="nav-movil flex items-center gap-3 rounded-xl px-4 py-4 text-lg font-medium text-masa-800 hover:bg-crema-100 active:bg-crema-200">
            <span class="text-masa-500">${iconoAyuda}</span>
            <span class="flex-1">${tituloAyuda}</span>
            <span class="text-masa-300">${iconoFlechaDer}</span>
          </a>
        </nav>
        <div class="mx-2 mb-2">${botonAvisosHtml()}</div>
        <button type="button" data-tema-boton class="mx-2 mb-2 flex w-[calc(100%-1rem)] items-center gap-3 rounded-xl border border-crema-200 px-4 py-3 text-left font-medium text-masa-800 hover:bg-crema-100"></button>
        <div class="flex items-center justify-between border-t border-crema-200 px-4 py-3">
          <span class="min-w-0 flex-1 truncate pr-2 text-sm text-masa-500">Sesión: <b>${s.email ?? s.usuario}</b></span>
          <button id="salirMovil" class="btn-sec">Salir</button>
        </div>
      </div>`;
  document.body.appendChild(menuMovil);
  const abrir = () => menuMovil.classList.remove("hidden");
  const cerrar = () => menuMovil.classList.add("hidden");
  nav.querySelector("#hamb")?.addEventListener("click", abrir);
  menuMovil.querySelector("#cerrarMenu")?.addEventListener("click", cerrar);
  menuMovil.querySelector("#backdrop")?.addEventListener("click", cerrar);
  menuMovil.querySelectorAll("a[data-hash]").forEach((a) => a.addEventListener("click", cerrar));

  const salir = async () => { await api.post("/api/logout"); location.reload(); };
  nav.querySelector("#salir")?.addEventListener("click", salir);
  menuMovil.querySelector("#salirMovil")?.addEventListener("click", salir);

  // El boton de las notificaciones del celular (la logica vive en notificaciones.ts).
  engancharBotonAvisos(menuMovil);
  // Modo claro / oscuro: un boton en el menu de la cuenta (escritorio) y otro
  // en el menu ☰ (celular). El de la cuenta NO cierra el desplegable al
  // tocarlo: se ve el cambio con el menu abierto.
  engancharBotonesTema(document);

  marcarActivo();
  window.addEventListener("hashchange", marcarActivo);
}

/** Mantiene al dia el numero de la campana. La logica vive en notificaciones.ts. */
function seguirAvisos(): void {
  void refrescarContador();
  window.setInterval(() => void refrescarContador(), 60000);
}

async function iniciar(): Promise<void> {
  try {
    const s = await api.get<Sesion>("/api/sesion");
    const permitidas = rutasPermitidas(s);
    renderNav(s, permitidas);
    const primera = permitidas.find((r) => !r.oculta)?.hash ?? "#/ayuda";
    if (!location.hash || !permitidas.some((r) => r.hash === location.hash)) {
      location.hash = primera;
    }
    iniciarRouter(permitidas, app);
    seguirAvisos();
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      nav.innerHTML = "";
      renderLogin(app);
    } else {
      app.innerHTML = `<div class="tarjeta text-red-700">Error al iniciar: ${
        e instanceof Error ? e.message : "desconocido"
      }</div>`;
    }
  }
}

// Los desplegables de la barra se cierran tocando afuera, con Escape o al
// cambiar de pantalla. Van aca, una sola vez, y no dentro de renderNav().
document.addEventListener("click", (ev) => {
  if (!(ev.target as HTMLElement).closest("[data-grupo]")) cerrarDesplegables();
});
document.addEventListener("keydown", (ev) => {
  if (ev.key === "Escape") cerrarDesplegables();
});
window.addEventListener("hashchange", () => cerrarDesplegables());

// Exponer navegar por si alguna pagina lo necesita.
(window as unknown as { navegar: typeof navegar }).navegar = navegar;

// El service worker se registra ANTES del login: sin el registrado, el
// navegador no ofrece "instalar la app", y el encargado del local se topa con
// eso en la primera pantalla que ve.
void registrarServiceWorker();
// Cuando llega un push con la app abierta, la pantalla se pone al dia sola.
escucharPush();
void iniciar();
